/**
 * Typisierte Hooks für `/api/v1/access` (Spec 05a, `domains/access`) —
 * Personen einladen, Zugriff geben/ändern/entziehen, die eigene Person und
 * die Rollenliste.
 *
 * `fetch` steht ausschließlich hier — nie in einer Komponente
 * (`apps/web/CLAUDE.md`). Query-Keys kommen ausschließlich aus
 * `lib/query-keys.ts`. Grant-Mutationen invalidieren den gesamten Querycache:
 * Zugriff beeinflusst neben Personen/Zugriffen auch Köpfe, Beziehungslinks
 * und sämtliche sichtbarkeitsgefilterten Fachlisten. Einzelne Keys würden
 * zuvor geladene Projektionen mit veralteten Rechten zurücklassen.
 *
 * Die Objektauswahl im Einladen-Sheet (`access-invite-entity`) liegt seit
 * Tor 2 von 006a nicht mehr hier: `components/EntityPicker.tsx` teilt sich
 * jetzt mit `task-cost-center`, die Abfrage dahinter mit `lib/entities.ts`
 * (Befund „Simplifier-Fund", `docs/work/006a-aufgaben-2a164c/findings.md`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { components } from '@/lib/api-types'
import { request } from '@/lib/api'
import { keys } from '@/lib/query-keys'

export type MeResponse = components['schemas']['MeResponse']
export type RoleOption = components['schemas']['RoleOption']
export type PersonListItem = components['schemas']['PersonListItem']
export type PersonDetail = components['schemas']['PersonDetail']
export type PersonCreate = components['schemas']['PersonCreate']
/** Spec 25, Fachregel 2 — Rumpf von `PATCH /access/users/{id}`. Jedes Feld
 * optional; nur tatsächlich geänderte Felder gehören in den Rumpf
 * (`AccessPersonSheet`, AK-1012). */
export type PersonUpdate = components['schemas']['PersonUpdate']
/** Spec 25, Fachregel 7 — `SecretStr` im Vertrag, hier ein einfacher String:
 * Das Passwort verlässt den Client ohnehin nur in diesem einen Rumpf. */
export type PasswordSet = components['schemas']['PasswordSet']
export type PersonGrant = components['schemas']['PersonGrant']
export type GrantItem = components['schemas']['GrantItem']
export type GrantCreate = components['schemas']['GrantCreate']
export type GrantUpdate = components['schemas']['GrantUpdate']
export type GrantsListResponse = components['schemas']['GrantsListResponse']
/** Spec 05b, AK-382 — die beiden Personenarten mit Beschriftung
 * (`GET /access/person-kinds`), dieselbe Bauform wie `RoleOption`. */
export type PersonKindOption = components['schemas']['LabeledValue']
/** Spec 05b, AK-368 — der `person`-Block von `GET /access/users/{id}/grants`. */
export type PersonHead = components['schemas']['PersonHead']
/** Spec 05b, AK-368 — eine Zeile der Rückrichtung. */
export type UserGrantItem = components['schemas']['UserGrantItem']
export type UserGrantsResponse = components['schemas']['UserGrantsResponse']

interface QueryOptions {
  readonly enabled?: boolean
}

/** `GET /access/me` — wer bin ich. Bestimmt u. a., ob `account-access` im
 * Konto-Menü erscheint (`is_external`, `router.tsx::AccountMenu`). */
export function useMe(options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.access.me,
    queryFn: ({ signal }) => request<MeResponse>('/access/me', { signal }),
    enabled: options.enabled ?? true,
  })
}

/** `GET /access/roles` — ändert sich zur Laufzeit nie, dieselbe Zurückhaltung
 * wie `useLegalForms`/`useSalutations` (`features/tenants/api.ts`). Kein
 * Client kennt die Rangfolge der Rollen selbst (I9). */
export function useRoles() {
  return useQuery({
    queryKey: keys.access.roles,
    queryFn: ({ signal }) => request<RoleOption[]>('/access/roles', { signal }),
    staleTime: Infinity,
  })
}

interface PersonsPage {
  readonly items: PersonListItem[]
  readonly next_cursor: string | null
}

/** `GET /access/users` — für ~10 interne Nutzer plus einzelne Externe ohne
 * „mehr laden" (dieselbe Zurückhaltung wie `useEntitiesTree`, eine Seite
 * reicht in dieser Ausbaustufe). Liefert für einen externen Aufrufer 200 mit
 * leerer Liste (Spec „Lesen: intern sieht, extern entdeckt nichts") — kein
 * Sonderfall im Client, die leere Liste trägt den Leerzustand von selbst.
 *
 * `q` filtert seit AK-381 **serverseitig** (Name und E-Mail, ohne Rücksicht
 * auf Groß-/Kleinschreibung) — nötig, sobald mehr Personen existieren als
 * `DEFAULT_PAGE_SIZE` (50): Ein Client-Filter über die geladene erste Seite
 * findet einen Treffer auf Seite 2 nicht (K-B1, `13-gatekeeper.md`). Für
 * `screen-access` (volle Liste) bleibt `q` leer, `PersonPicker` ruft mit dem
 * getippten Text — dieselbe Bauform wie `useTenantOptions`. */
export function useAccessUsers(q = '', options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.access.users(q),
    queryFn: ({ signal }) => request<PersonsPage>(`/access/users?q=${encodeURIComponent(q)}`, { signal }),
    enabled: options.enabled ?? true,
  })
}

/** `GET /access/person-kinds` — dieselbe Zurückhaltung wie `useRoles`: kein
 * Datenbankzugriff, ändert sich zur Laufzeit nie (Spec 05b, AK-382). Speist
 * das Auswahlfeld „Art" in `AccessInviteSheet` statt der bisher fest
 * verdrahteten Wörter „Intern"/„Extern". */
export function usePersonKinds() {
  return useQuery({
    queryKey: keys.access.personKinds,
    queryFn: ({ signal }) => request<PersonKindOption[]>('/access/person-kinds', { signal }),
    staleTime: Infinity,
  })
}

/** `GET /access/users/{user_id}/grants` — die Rückrichtung zu `useAccessGrants`
 * (Spec 05b, AK-368): nicht „wer hat Zugriff auf dieses Objekt", sondern
 * „welche Objekte sieht diese Person". Trägt den Kopf der Personenansicht im
 * `person`-Block mit, damit `screen-access-user` auch bei direktem Aufruf der
 * URL nichts nachladen muss. */
export function useUserGrants(userId: string, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.access.userGrants(userId),
    queryFn: ({ signal }) => request<UserGrantsResponse>(`/access/users/${userId}/grants`, { signal }),
    enabled: options.enabled ?? true,
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: PersonCreate) =>
      request<PersonDetail>('/access/users', {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries()
    },
  })
}

/** `GET /access/grants?entity_id=` — direkte und geerbte Zugriffe eines
 * Objekts, für `access-sheet` (Spec 05a, „Sheet Zugriff am Objekt"). */
export function useAccessGrants(entityId: string, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.access.grants(entityId),
    queryFn: ({ signal }) => request<GrantsListResponse>(`/access/grants?entity_id=${entityId}`, { signal }),
    enabled: options.enabled ?? true,
  })
}

export function useCreateGrant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: GrantCreate) =>
      request<GrantItem>('/access/grants', {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries()
    },
  })
}

interface UpdateGrantArgs {
  readonly entityId: string
  readonly userId: string
  readonly body: GrantUpdate
  readonly ifMatch: string
}

export function useUpdateGrant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ entityId, userId, body, ifMatch }: UpdateGrantArgs) =>
      request<GrantItem>(`/access/grants/${entityId}/${userId}`, {
        method: 'PATCH',
        body,
        idempotencyKey: crypto.randomUUID(),
        ifMatch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries()
    },
  })
}

interface DeleteGrantArgs {
  readonly entityId: string
  readonly userId: string
}

/** `DELETE /access/grants/{entity_id}/{user_id}` — **kein** `If-Match` (Spec
 * „Optimistisches Sperren auf Grants": „Der Entzug ist absichtsvoll und hat
 * kein Ergebnis, das ein Zweiter versehentlich überschreiben könnte"). */
export function useDeleteGrant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ entityId, userId }: DeleteGrantArgs) =>
      request(`/access/grants/${entityId}/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries()
    },
  })
}

interface UpdateUserArgs {
  readonly userId: string
  readonly body: PersonUpdate
}

/** `PATCH /access/users/{user_id}` — Name, E-Mail, Vollzugriff (Spec 25,
 * Fachregel 2). Kein `If-Match` (R5): Der Aufrufer entscheidet, welche Felder
 * überhaupt in `body` stehen (nur die geänderten, AK-1012) — diese Funktion
 * sendet sie unverändert weiter. Invalidiert wie jede Zugriffsänderung den
 * gesamten Querycache (Vollzugriff/Sperre wirken in `permissions/`, nicht nur
 * auf die Person selbst). */
export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, body }: UpdateUserArgs) =>
      request<PersonListItem>(`/access/users/${userId}`, {
        method: 'PATCH',
        body,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries()
    },
  })
}

interface SetPasswordArgs {
  readonly userId: string
  readonly password: string
}

/** `POST /access/users/{user_id}/password` — 204, kein Datenbankschreiben
 * (Spec 25, Fachregel 2: „ein Passwort ist kein Stammdatum"). Keine
 * Invalidierung: Es gibt keine angezeigte Projektion, die sich dadurch
 * ändert. */
export function useSetPassword() {
  return useMutation({
    mutationFn: ({ userId, password }: SetPasswordArgs) =>
      request<undefined>(`/access/users/${userId}/password`, {
        method: 'POST',
        body: { password } satisfies PasswordSet,
        idempotencyKey: crypto.randomUUID(),
      }),
  })
}

/** `POST /access/users/{user_id}/disable` — sperrt sofort (Spec 25,
 * Fachregel 2/5). Antwort ist der neue `PersonListItem`-Satz; die
 * Invalidierung holt ihn an jeder geladenen Stelle nach (Liste, Kopf). */
export function useDisableUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) =>
      request<PersonListItem>(`/access/users/${userId}/disable`, {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries()
    },
  })
}

/** `POST /access/users/{user_id}/enable` — hebt die Sperre auf. */
export function useEnableUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) =>
      request<PersonListItem>(`/access/users/${userId}/enable`, {
        method: 'POST',
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries()
    },
  })
}

/** `POST /access/me/password` — das eigene Passwort, ohne aktuelles Passwort
 * (Spec 25, Fachregel 4/R4). Keine Invalidierung: nichts Angezeigtes ändert
 * sich dadurch. */
export function useChangeOwnPassword() {
  return useMutation({
    mutationFn: (password: string) =>
      request<undefined>('/access/me/password', {
        method: 'POST',
        body: { password } satisfies PasswordSet,
        idempotencyKey: crypto.randomUUID(),
      }),
  })
}
